/**
 * Side-by-side comparison of two or more analyzed documents.
 *
 * Two modes over one selection, because there are two different questions.
 *
 * **Values** lines up what was extracted, and is the mode that reasons: the table,
 * the difference tinting and the plain-language question all live here.
 *
 * **Visual** does not reason at all. It puts the sheets next to each other and
 * gets out of the way - whole sheet against whole sheet, or one chosen region
 * against another, at a size worth looking at. No model, no question box, nothing
 * computed: everything it shows is already in the browser from the extraction, so
 * it is instant and there is nothing to ask for. Asking about drawings belongs in
 * Values and is not duplicated here.
 *
 * Regions are picked per column rather than paired automatically. They are
 * numbered in detection order per document, so region 3 in one sheet is not
 * necessarily region 3 in another - pairing them silently would line up unrelated
 * views and present it as a comparison. The user says which two to put side by
 * side, because only they can know.
 *
 * Values is the default: it is the one that answers most questions.
 *
 * A screen of its own in the side navigation, and reachable with nothing selected -
 * which is the point of it being a tab. Comparing was previously only a mode of the
 * library, entered from a button that appears once two rows are ticked, so the
 * feature was invisible to anyone who had not already discovered the checkboxes. As
 * a tab it can be found first and explain itself second: with fewer than two
 * documents selected it says what to select, rather than not existing.
 *
 * The selection still lives in the library, and still survives leaving here.
 *
 * The table is deliberately plain - one row per attribute, one column per document,
 * differences tinted. What the attributes *are* comes from the prompts, so this
 * screen cannot assume anything about them beyond their being label/value pairs.
 */
import { useMemo, useRef, useState } from "react";

import type { DocumentDetail, DocumentSummary, Position } from "../api";
import { buildComparison, compareChosen } from "../compare";
import type { CompareCell, CompareRow } from "../compare";
import { EmptyState } from "../components/DataGrid";
import { Badge, Button, Icon, IconButton, Spinner, Switch } from "../components/primitives";
import { formatDate, imageSrc, regionClass, shortName } from "../format";
import { Markdown } from "../markdown";

/** Which comparison is on screen: the extracted values, or the drawings. */
export type CompareMode = "values" | "visual";

interface CompareProps {
  documents: DocumentDetail[];
  /** True while a selected document's extraction is still being fetched. */
  loading: boolean;
  onlyDifferences: boolean;
  onToggleOnlyDifferences: (next: boolean) => void;
  onOpen: (id: string) => void;
  onBack: () => void;
  /**
   * Every document in the library, so the choice can be made here.
   *
   * Without this the screen could only report a selection made elsewhere, which
   * meant landing on it with nothing ticked and being told to go away, tick two
   * rows and come back.
   */
  library: DocumentSummary[];
  /** The chosen ids, in the order their columns appear. */
  selected: string[];
  onToggle: (id: string) => void;
  /** The plain-language question about the selected documents. */
  question: string;
  /** The last answer, or null when none has been asked for this selection. */
  answer: string | null;
  asking: boolean;
  askError: string | null;
  onQuestionChange: (value: string) => void;
  onAsk: () => void;
  mode: CompareMode;
  onModeChange: (next: CompareMode) => void;
}

export function Compare({
  documents,
  loading,
  onlyDifferences,
  onToggleOnlyDifferences,
  onOpen,
  onBack,
  library,
  selected,
  onToggle,
  question,
  answer,
  asking,
  askError,
  onQuestionChange,
  onAsk,
  mode,
  onModeChange,
}: CompareProps) {
  const comparison = useMemo(() => buildComparison(documents), [documents]);
  const visual = mode === "visual";

  /**
   * Which region each column is framed on, or -1 for the whole sheet.
   *
   * Held here rather than inside the side-by-side view because the values table
   * drives it: clicking a difference frames the region it was read from, in every
   * column, and switches to the sheets. The table and the sheets are two views of
   * one comparison, so the thing they are both about lives above both of them.
   */
  const [chosen, setChosen] = useState<number[]>(() => documents.map(() => -1));

  // The selection can change under us - a document removed, another ticked - and a
  // region index from the old column would then frame the wrong thing.
  const signature = documents.map((document) => document.id).join("|");
  const lastSignature = useRef(signature);
  if (lastSignature.current !== signature) {
    lastSignature.current = signature;
    setChosen(documents.map(() => -1));
  }

  const chooseRegion = (column: number, region: number) =>
    setChosen((current) => {
      const next = [...current];
      next[column] = region;
      return next;
    });

  /**
   * Show one row's region on the sheets.
   *
   * Regions are lined up across documents by index - the same assumption the
   * table itself is built on - so the same index frames every column. The
   * comparison warns when the counts differ, and that warning applies here too.
   */
  const showOnSheets = (regions: (number | undefined)[]) => {
    // Each column gets its own region, because the same drawing is region 2 in
    // one sheet and region 3 in the other. A column that does not have this
    // drawing at all is framed on the whole sheet, which is the honest picture of
    // "it is not here".
    setChosen(documents.map((_, column) => regions[column] ?? -1));
    onModeChange("visual");
  };

  // Open only when there is nothing to show yet. Arriving with two documents
  // already ticked, the comparison is what the screen is for and the picker is a
  // control that has done its job.
  const [pickerOpen, setPickerOpen] = useState(selected.length < 2);

  const visibleRows = onlyDifferences
    ? comparison.rows.filter((row) => row.differs)
    : comparison.rows;

  // Only render a heading for a section that still has rows under it.
  const groupsWithRows = comparison.groups.filter((group) =>
    visibleRows.some((row) => row.group === group),
  );

  return (
    <section className="screen">
      <div className="screen__head">
        <IconButton icon="arrow_back" label="Back to the library" onClick={onBack} />
        <h1 className="screen__title">Compare</h1>
        {/*
          The badge counts differing rows, which is a fact about the table. Visual
          compares nothing and asserts nothing - it only shows - so there is no
          number it could honestly carry, and it is dropped rather than shown
          meaning something else.
        */}
        {!visual && documents.length > 0 && (
          <Badge
            tone={comparison.differingRows > 0 ? "warning" : "success"}
            label={
              comparison.differingRows > 0
                ? `${comparison.differingRows} of ${comparison.rows.length} differ`
                : "Identical"
            }
          />
        )}
        <span className="u-spacer" />
        <ModeToggle mode={mode} onChange={onModeChange} />
        {/* Serves both modes: in Values it filters the table, in Visual the panel
            beside the sheets. It is the same question either way - "just show me
            what is not the same" - so it stays one control in one place. */}
        <Switch
          label="Only differences"
          checked={onlyDifferences}
          onChange={onToggleOnlyDifferences}
        />
      </div>

      <div className="screen__body u-scroll">
        {/*
          The picker is the first thing on the screen, so comparing starts here
          rather than in the library. It stays visible with a comparison already on
          screen, because swapping one document for another is the commonest next
          thing to want and going back to the grid to do it loses the question.
        */}
        {library.length > 0 && (
          <div className="picker">
            <div className="askbox__head">
              <span className="eyebrow" style={{ marginBottom: 0 }}>
                Documents to compare
              </span>
              <span className="u-spacer" />
              <span className="screen__caption">
                {/*
                  The library total is here because the list scrolls: without it a
                  view of the first four rows reads as "I only have four documents"
                  rather than "there are more below".
                */}
                {selected.length < 2
                  ? `Pick two or more of ${library.length}.`
                  : `${selected.length} of ${library.length} picked - columns follow this order.`}
              </span>
              {/*
                Collapsing is not a nicety. The list is one full row per document in
                the library, so with a handful of drawings it pushed the comparison
                itself off the bottom of the screen - the picker filled the space
                that was meant for the thing being picked.
              */}
              <Button
                size="sm"
                icon={pickerOpen ? "expand_less" : "expand_more"}
                onClick={() => setPickerOpen((open) => !open)}
              >
                {pickerOpen ? "Done" : "Change"}
              </Button>
            </div>

            {pickerOpen ? (
              /*
                Laid out as rows with the same columns as the library grid - preview,
                name, regions, values, date - so the list of documents looks the same
                wherever it appears and a row is recognised rather than re-read. It
                scrolls inside itself, so a library of fifty behaves like a library of
                five.
              */
              <div className="picker__list u-scroll" role="group" aria-label="Documents to compare">
                {library.map((document) => {
                  const position = selected.indexOf(document.id);
                  const picked = position >= 0;
                  return (
                    <button
                      key={document.id}
                      className={`picker__row ${picked ? "is-picked" : ""}`.trim()}
                      onClick={() => onToggle(document.id)}
                      aria-pressed={picked}
                    >
                      {/* The tick carries its column number: selection order decides
                          column order, and this is where that becomes visible. */}
                      <span className="picker__mark">{picked ? position + 1 : ""}</span>
                      {document.thumb ? (
                        <img className="grid-thumb" src={`data:image/jpeg;base64,${document.thumb}`} alt="" />
                      ) : (
                        <span className="grid-thumb grid-thumb--empty">
                          <Icon name="description" size={16} />
                        </span>
                      )}
                      <span className="picker__name u-truncate" title={document.name}>
                        {document.name}
                      </span>
                      <span className="picker__num">{document.n_positions} regions</span>
                      <span className="picker__num">{document.n_fields} values</span>
                      <span className="picker__num picker__num--date">{formatDate(document.created)}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              /*
                Collapsed, the picked documents stay visible as chips. What is being
                compared is the one thing that must never be guessed at, and a chip
                still carries its column number and can still be removed - so folding
                the list away costs no control, only space.
              */
              <div className="picker__chips">
                {selected.length === 0 ? (
                  <span className="screen__caption">Nothing picked yet.</span>
                ) : (
                  selected.map((id, position) => {
                    const document = library.find((row) => row.id === id);
                    return (
                      <span key={id} className="picker__chip">
                        <span className="picker__mark">{position + 1}</span>
                        <span className="u-truncate" title={document?.name ?? id}>
                          {shortName(document?.name ?? id, 30)}
                        </span>
                        <IconButton
                          icon="close"
                          label={`Remove ${document?.name ?? id} from the comparison`}
                          size="sm"
                          onClick={() => onToggle(id)}
                        />
                      </span>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}

        {/*
          Asking sits above the table rather than replacing it. The table is the
          verifiable form - every value as extracted, differences tinted - and the
          answer is a reading of it, so having both on screen means a claim in the
          prose can be checked against the row it came from without leaving.
        */}
        {/*
          Asking sits above the table rather than replacing it. The table is the
          verifiable form - every value as extracted, differences tinted - and the
          answer is a reading of it, so having both on screen means a claim in the
          prose can be checked against the row it came from without leaving.

          Values only. Visual is a viewer, not an answer: it shows the sheets and
          says nothing about them, and a second question box there would be the
          same feature twice over the same selection.
        */}
        {!visual && selected.length >= 2 && (
          <div className="askbox">
            <div className="askbox__head">
              <span className="eyebrow" style={{ marginBottom: 0 }}>
                Ask about these drawings
              </span>
              <span className="u-spacer" />
              <span className="screen__caption">
                Answered only from what was extracted from the {selected.length} picked
                documents.
              </span>
            </div>

            <textarea
              className="composer-input"
              rows={2}
              placeholder="Ask in plain language - or leave this empty for a general comparison."
              value={question}
              disabled={asking}
              onChange={(event) => onQuestionChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  onAsk();
                }
              }}
            />

            <div className="u-row">
              <Button
                variant="primary"
                icon="compare_arrows"
                onClick={onAsk}
                disabled={asking || loading}
              >
                {asking ? "Reading..." : "Ask"}
              </Button>
              {loading && !asking && (
                <span className="screen__caption">Waiting for a document to load...</span>
              )}
            </div>

            {askError && <div className="askbox__error">{askError}</div>}

            {asking && !answer && (
              <div className="askbox__answer">
                <Spinner label="Comparing the extracted values..." />
              </div>
            )}

            {answer && (
              <div className="askbox__answer">
                <Markdown text={answer} />
              </div>
            )}
          </div>
        )}

        {/*
          Gated on the selection rather than on how many extractions have arrived:
          those load one at a time, and "pick two documents" is the wrong thing to
          say to someone who has picked two and is waiting for the second to fetch.
        */}
        {library.length === 0 ? (
          <EmptyState
            icon="description"
            title="Nothing to compare yet"
            text="Analyze at least two PDF drawings and they can be lined up side by side here, with the differences highlighted."
            action={<Button onClick={onBack}>Go to the library</Button>}
          />
        ) : selected.length < 2 ? (
          <EmptyState
            icon="compare_arrows"
            title="Pick at least two documents"
            text="Choose two or more above. Their extracted values line up side by side, with the differences highlighted, and you can ask about them in plain language - or switch to Visual to put the sheets themselves next to each other."
          />
        ) : visual ? (
          documents.length < selected.length ? (
            <div className="empty">
              <Spinner label="Loading the picked drawings..." />
            </div>
          ) : (
            <SideBySide
              chosen={chosen}
              onChoose={chooseRegion}
              documents={documents}
              onOpen={onOpen}
              onToggle={onToggle}
              onlyDifferences={onlyDifferences}
              onShowAll={() => onToggleOnlyDifferences(false)}
            />
          )
        ) : documents.length < 2 ? (
          <div className="empty">
            <Spinner label="Loading the picked documents..." />
          </div>
        ) : visibleRows.length === 0 ? (
          <EmptyState
            icon={onlyDifferences ? "done_all" : "search_off"}
            title={onlyDifferences ? "No differences" : "Nothing to compare"}
            text={
              onlyDifferences
                ? "Every extracted value matches across these documents."
                : "These documents have no extracted values between them, so there is nothing to line up."
            }
            action={
              onlyDifferences ? (
                <Button onClick={() => onToggleOnlyDifferences(false)}>Show all values</Button>
              ) : undefined
            }
          />
        ) : (
          <>
            {loading && (
              <div className="searchnote">
                <span>Still loading one of the selected documents...</span>
              </div>
            )}
            {comparison.unmatchedDrawings > 0 && (
              <div className="searchnote">
                <span>
                  {comparison.unmatchedDrawings === 1
                    ? "One drawing appears on only one of these sheets, so its section has values in one column and nothing in the other."
                    : `${comparison.unmatchedDrawings} drawings appear on only some of these sheets, so their sections have values in one column and nothing in the others.`}{" "}
                  Sections are matched by the name the sheet gives each drawing.
                </span>
              </div>
            )}

            <div className="compare">
              <table className="compare__table">
                <thead>
                  <tr>
                    <th className="compare__attr compare__attr--head">Attribute</th>
                    {documents.map((document) => (
                      <th key={document.id} className="compare__doc">
                        <button
                          className="compare__docname u-truncate"
                          title={`Open ${document.name}`}
                          onClick={() => onOpen(document.id)}
                        >
                          {shortName(document.name, 34)}
                        </button>
                        <IconButton
                          icon="close"
                          label={`Remove ${document.name} from the comparison`}
                          size="sm"
                          onClick={() => onToggle(document.id)}
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupsWithRows.map((group) => (
                    <Section
                      key={group}
                      group={group}
                      rows={visibleRows.filter((row) => row.group === group)}
                      columns={documents.length}
                      onShowOnSheets={showOnSheets}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * The Values / Visual switch.
 *
 * Two buttons rather than a toggle, because the pair are not on and off of one
 * thing: they are two different comparisons, and a switch labelled "Visual" would
 * suggest the table gains pictures rather than being replaced by a different
 * answer to a different question.
 */
function ModeToggle({
  mode,
  onChange,
}: {
  mode: CompareMode;
  onChange: (next: CompareMode) => void;
}) {
  return (
    <div className="modetoggle" role="group" aria-label="What to compare">
      <button
        className={`modetoggle__option ${mode === "values" ? "is-active" : ""}`.trim()}
        onClick={() => onChange("values")}
        aria-pressed={mode === "values"}
        title="Line up the values that were extracted from each drawing"
      >
        <Icon name="table_rows" size={16} />
        Values
      </button>
      <button
        className={`modetoggle__option ${mode === "visual" ? "is-active" : ""}`.trim()}
        onClick={() => onChange("visual")}
        aria-pressed={mode === "visual"}
        title="Put the sheets side by side, whole or region by region"
      >
        <Icon name="image_search" size={16} />
        Visual
      </button>
    </div>
  );
}

/**
 * The sheets next to each other, with their regions marked, and the values of
 * whatever is framed beside them.
 *
 * One column per picked document, each independently framed on the whole sheet or
 * on one of its detected regions. Everything here comes from data the browser
 * already holds - the archived page images, each region's normalised box, and the
 * fields read out of it - so there is no request, no model and no waiting.
 *
 * The regions are drawn on the sheets rather than only listed in the picker. A box
 * on the drawing is the only thing that says *where* region 7 is, and picking a
 * region to compare is a question about the drawing, not about a dropdown - so the
 * boxes are clickable and the dropdown follows them.
 *
 * The right-hand panel is what makes it a comparison rather than two pictures: it
 * lines up the values of the two framed things, using the same union / first-seen /
 * differs rules as the Values tab, so a difference the eye finds on the sheet can
 * be read as a number without leaving.
 */
function SideBySide({
  documents,
  onOpen,
  onToggle,
  onlyDifferences,
  onShowAll,
  chosen,
  onChoose,
}: {
  documents: DocumentDetail[];
  onOpen: (id: string) => void;
  onToggle: (id: string) => void;
  onlyDifferences: boolean;
  onShowAll: () => void;
  /** One entry per column: the framed region's index, or -1 for the whole sheet. */
  chosen: number[];
  onChoose: (column: number, region: number) => void;
}) {
  const [showRegions, setShowRegions] = useState(true);
  const [scope, setScope] = useState<"framed" | "all">("framed");

  const choose = onChoose;

  const framed = useMemo(() => compareChosen(documents, chosen), [documents, chosen]);
  const everything = useMemo(() => buildComparison(documents), [documents]);
  const comparison = scope === "all" ? everything : framed;

  // Nothing framed yet: every sheet is still showing as a whole. In "Framed" the
  // panel then says how to pick a region instead of listing the whole sheets'
  // values, which is what "Everything" is for.
  const nothingChosen = documents.every((_, column) => (chosen[column] ?? -1) < 0);
  const choosing = scope === "framed" && nothingChosen;

  const rows = onlyDifferences
    ? comparison.rows.filter((row) => row.differs)
    : comparison.rows;
  const groups = comparison.groups.filter((group) => rows.some((row) => row.group === group));

  /** What a column's values are of, said in the panel where the sheet is not. */
  const framing = (column: number) => {
    if (scope === "all") return "whole document";
    const index = chosen[column] ?? -1;
    return index < 0 ? "whole sheet" : `R${index + 1}`;
  };

  return (
    <div className="sbs">
      {/*
        Wraps rather than divides. A fixed column per document turns four drawings
        into four slivers on the same screen width; a minimum pane width means the
        fifth sheet goes onto a second row and every sheet stays legible.
      */}
      <div className="sbs__sheets">
        {documents.map((document, column) => (
          <SheetPane
            key={document.id}
            document={document}
            column={column}
            region={chosen[column] ?? -1}
            onChoose={(region) => choose(column, region)}
            showRegions={showRegions}
            onOpen={onOpen}
            onToggle={onToggle}
          />
        ))}
      </div>

      <aside className="sbs__values">
        <div className="sbs__valhead">
          <span className="eyebrow" style={{ marginBottom: 0 }}>
            {scope === "all" ? "All extracted values" : "Framed region"}
          </span>
          <span className="u-spacer" />
          {!choosing && comparison.rows.length > 0 && (
            <Badge
              tone={comparison.differingRows > 0 ? "warning" : "success"}
              label={
                comparison.differingRows > 0
                  ? `${comparison.differingRows} of ${comparison.rows.length} differ`
                  : "All match"
              }
            />
          )}
        </div>

        {/*
          What each column is currently showing, spelled out once at the top rather
          than repeated on every row. This is the line that says the panel is a
          comparison of R2 against R1 and not a readout of one drawing.
        */}
        <div className="sbs__valwho">
          {documents.map((document, column) => (
            <span key={document.id} className="sbs__who">
              <span className="sbs__ord">{column + 1}</span>
              <span className="u-truncate" title={document.name}>
                {shortName(document.name, 22)}
              </span>
              <span className="sbs__whopart">{framing(column)}</span>
            </span>
          ))}
        </div>

        <div className="sbs__valcontrols">
          <div className="modetoggle" role="group" aria-label="Which values to compare">
            <button
              className={`modetoggle__option ${scope === "framed" ? "is-active" : ""}`.trim()}
              onClick={() => setScope("framed")}
              aria-pressed={scope === "framed"}
            >
              Framed
            </button>
            <button
              className={`modetoggle__option ${scope === "all" ? "is-active" : ""}`.trim()}
              onClick={() => setScope("all")}
              aria-pressed={scope === "all"}
            >
              Everything
            </button>
          </div>
          <span className="u-spacer" />
          <Switch label="Boxes" checked={showRegions} onChange={setShowRegions} />
        </div>

        <div className="sbs__valbody u-scroll">
          {choosing ? (
            <div className="sbs__valempty">
              <div className="u-row">
                <Icon name="ads_click" size={18} className="u-muted" />
                <strong>Choose a region to compare</strong>
              </div>
              <p className="screen__caption" style={{ margin: 0 }}>
                Click a framed region on either sheet, or pick one from the list above it. Both
                sheets zoom to it, and its values line up here side by side.
              </p>
              <Button size="sm" onClick={() => setScope("all")}>
                Compare all values instead
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <div className="sbs__valempty">
              {comparison.rows.length > 0 && onlyDifferences ? (
                <>
                  <p className="screen__caption">
                    Everything read from what is framed matches across these drawings.
                  </p>
                  <Button size="sm" onClick={onShowAll}>
                    Show all values
                  </Button>
                </>
              ) : (
                <p className="screen__caption">
                  {scope === "all"
                    ? "These documents have no extracted values between them."
                    : "Nothing was read from what is framed. Pick a region on a sheet, or switch to Everything."}
                </p>
              )}
            </div>
          ) : (
            groups.map((group) => (
              <div key={group} className="sbs__valgroup">
                {/* Only worth a heading when there is more than one section to tell
                    apart, which "Everything" is and a single framed region is not. */}
                {groups.length > 1 && <div className="sbs__valgrouphead">{group}</div>}
                {rows
                  .filter((row) => row.group === group)
                  .map((row) => (
                    <ValueRow
                      key={`${group}/${row.label}`}
                      label={row.label}
                      cells={row.cells}
                      differs={row.differs}
                    />
                  ))}
              </div>
            ))
          )}
        </div>
      </aside>
    </div>
  );
}

/**
 * One attribute, with each document's value stacked under it.
 *
 * Stacked rather than a column per document, which is what this started as and why
 * it had to change: at the width of a side panel a three-column table pushed the
 * second drawing's values off the edge entirely, so the thing on screen was a
 * readout of one drawing wearing a comparison's clothes. Extracted values are
 * often a sentence rather than a number - "Right-side table is cropped; only
 * partial labels are visible" - and a sentence needs the full width whichever
 * drawing it came from.
 *
 * Stacking also puts the two values on consecutive lines, which is the arrangement
 * that makes a difference between them obvious at a glance.
 */
function ValueRow({
  label,
  cells,
  differs,
}: {
  label: string;
  cells: CompareCell[];
  differs: boolean;
}) {
  return (
    <div className={`vrow ${differs ? "vrow--diff" : ""}`.trim()}>
      <div className="vrow__label">{label}</div>
      {cells.map((cell, column) => (
        <div key={column} className="vrow__val">
          <span className="vrow__ord">{column + 1}</span>
          {cell.present ? (
            <span className="vrow__text">{cell.text}</span>
          ) : (
            <span className="vrow__text u-muted">not recorded</span>
          )}
        </div>
      ))}
    </div>
  );
}

/** One column: a document, what part of it to show, and that part shown. */
function SheetPane({
  document,
  column,
  region,
  onChoose,
  showRegions,
  onOpen,
  onToggle,
}: {
  document: DocumentDetail;
  column: number;
  region: number;
  onChoose: (region: number) => void;
  showRegions: boolean;
  onOpen: (id: string) => void;
  onToggle: (id: string) => void;
}) {
  const [page, setPage] = useState(0);
  // The page image's own aspect ratio, learned when it loads. The stage is set to
  // it so the stage box is exactly the image box - which is what lets a region be
  // positioned as a percentage and land on the right part of the drawing.
  const [ratio, setRatio] = useState<number | null>(null);

  const positions = document.data.positions ?? [];
  const pages = document.pages;
  const active = region >= 0 ? positions[region] : undefined;

  // A chosen region decides which page is shown: picking R7 and being left on page
  // 1 while it lives on page 3 would look like the region had vanished.
  // `source_page` is 1-based in the extraction; the viewer indexes from 0.
  const shown = active ? Math.max(0, (active.source_page ?? 1) - 1) : page;
  const box = active?.bbox ?? null;

  const onThisPage = positions
    .map((position, index) => ({ position, index }))
    .filter(({ position }) => (position.source_page ?? 1) - 1 === shown);

  // Bring the box's centre to the middle of the pane, then magnify about that
  // middle until the larger of its two sides fills the frame - the "contain" rule
  // an image viewer uses, so the region is never cropped and never stretched.
  //
  // The translate is not optional. Scaling about the box's own centre alone leaves
  // that point exactly where it already was, so a title block in the corner stays
  // in the corner and grows straight out of the pane. Rightmost transform applies
  // first: the region is centred, and only then enlarged.
  const frame = box
    ? (() => {
        const [x0, y0, x1, y1] = box;
        const width = Math.max(0.02, x1 - x0);
        const height = Math.max(0.02, y1 - y0);
        return {
          transform:
            `scale(${1 / Math.max(width, height)}) ` +
            `translate(${(0.5 - (x0 + x1) / 2) * 100}%, ${(0.5 - (y0 + y1) / 2) * 100}%)`,
        };
      })()
    : undefined;

  return (
    <div className="sbs__pane">
      <div className="sbs__head">
        <span className="sbs__ord">{column + 1}</span>
        <button
          className="sbs__name u-truncate"
          title={`Open ${document.name}`}
          onClick={() => onOpen(document.id)}
        >
          {shortName(document.name, 28)}
        </button>
        <IconButton
          icon="close"
          label={`Remove ${document.name} from the comparison`}
          size="sm"
          onClick={() => onToggle(document.id)}
        />
      </div>

      <div className="sbs__controls">
        {/*
          A plain select rather than a row of chips: a sheet can carry dozens of
          regions, and this has to sit inside a column that may be one of four. It
          mirrors the boxes on the drawing, which are the other way to choose.
        */}
        <select
          className="sbs__select"
          value={region}
          onChange={(event) => onChoose(Number(event.target.value))}
          aria-label={`What to show from ${document.name}`}
        >
          <option value={-1}>Whole sheet</option>
          {positions.map((position, index) => (
            <option key={index} value={index} disabled={!position.bbox}>
              {regionLabel(position, index)}
            </option>
          ))}
        </select>

        {/* Only meaningful on the whole sheet: a region fixes its own page. */}
        {!active && pages.length > 1 && (
          <span className="sbs__pager">
            <IconButton
              icon="chevron_left"
              label="Previous page"
              size="sm"
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            />
            <span className="u-mono">
              {shown + 1}/{pages.length}
            </span>
            <IconButton
              icon="chevron_right"
              label="Next page"
              size="sm"
              onClick={() => setPage((current) => Math.min(pages.length - 1, current + 1))}
            />
          </span>
        )}
        {active && !box && (
          <span className="screen__caption">No box recorded; the whole sheet is shown.</span>
        )}
      </div>

      <div className="sbs__frame">
        {pages[shown] ? (
          <div
            className="sbs__stage"
            style={{ ...frame, aspectRatio: ratio ?? undefined }}
          >
            <img
              className="sbs__image"
              src={imageSrc(pages[shown])}
              onLoad={(event) =>
                setRatio(event.currentTarget.naturalWidth / event.currentTarget.naturalHeight)
              }
              alt={
                active
                  ? `${document.name}, region ${region + 1}`
                  : `${document.name}, page ${shown + 1}`
              }
            />
            {showRegions &&
              onThisPage.map(({ position, index }) => {
                const marks = position.bbox;
                if (!marks) return null;
                const [x0, y0, x1, y1] = marks;
                return (
                  <div
                    key={index}
                    className={regionClass(marks, index === region)}
                    style={{
                      left: `${x0 * 100}%`,
                      top: `${y0 * 100}%`,
                      width: `${(x1 - x0) * 100}%`,
                      height: `${(y1 - y0) * 100}%`,
                    }}
                    // Clicking the framed region again returns to the whole sheet,
                    // so zooming out needs no separate control.
                    onClick={() => onChoose(index === region ? -1 : index)}
                    role="button"
                    aria-pressed={index === region}
                    aria-label={`Region ${index + 1} of ${document.name}`}
                  >
                    {/* The tag is scaled by the frame's transform along with
                        everything else, so at 3x it would be a banner across the
                        drawing. Zoomed in there is only one region being looked at
                        and the picker already names it, so the outline is enough. */}
                    {!active && <span className="region__tag">R{index + 1}</span>}
                  </div>
                );
              })}
          </div>
        ) : (
          <span className="screen__caption">No page image is archived for this document.</span>
        )}
      </div>
    </div>
  );
}

/**
 * Label one region in the picker.
 *
 * "R3" alone is correct but useless when choosing what to line up against another
 * sheet. What it belongs to is the most useful thing that can be said about it -
 * "SECTION A-A / dimension chain" is pickable, "R14" is not - and a sheet now
 * yields a couple of dozen regions, so the list needs it. A record with no
 * grouping falls back to the region's first extracted value as a hint, which is
 * what the picker showed before; which value that is depends entirely on the
 * prompts, hence a hint and not a title.
 */
function regionLabel(position: Position, index: number): string {
  const drawing = position.group_label?.trim();
  if (drawing) {
    const part = position.label?.trim();
    const within = part && part.toLowerCase() !== drawing.toLowerCase() ? ` / ${part}` : "";
    return `R${index + 1} - ${shortName(`${drawing}${within}`, 34)}`;
  }
  const first = Object.values(position.fields ?? {})[0];
  const hint = typeof first === "string" ? first.trim() : "";
  return hint ? `R${index + 1} - ${shortName(hint, 24)}` : `R${index + 1}`;
}

function Section({
  group,
  rows,
  columns,
  onShowOnSheets,
}: {
  group: string;
  rows: CompareRow[];
  columns: number;
  /** Frame this section's region on every sheet and switch to them. */
  onShowOnSheets: (regions: (number | undefined)[]) => void;
}) {
  // Every row of a section comes from the same region, so the section is what
  // carries the number. The header section has none: it belongs to the sheet
  // rather than to a place on it, and there is nothing to point at.
  const regions = rows.find((row) => row.regions !== undefined)?.regions;

  return (
    <>
      <tr className="compare__row--group">
        <th colSpan={columns + 1}>
          <span className="compare__groupname">{group}</span>
          {regions !== undefined && (
            <button
              className="compare__locate"
              onClick={() => onShowOnSheets(regions)}
              title={`Frame ${group} on the sheets`}
            >
              <Icon name="my_location" size={14} />
              Show on the sheets
            </button>
          )}
        </th>
      </tr>
      {rows.map((row) => (
        <tr
          key={`${group}/${row.label}`}
          className={`${row.differs ? "compare__row--diff" : ""} ${row.regions !== undefined ? "compare__row--locatable" : ""}`.trim()}
          // The whole row, not just the button above it: the thing a reader wants
          // to see on the drawing is this difference, and they are already
          // pointing at it.
          onClick={row.regions ? () => onShowOnSheets(row.regions as (number | undefined)[]) : undefined}
          title={row.regions ? "Show this on the sheets" : undefined}
        >
          <th className="compare__attr">{row.label}</th>
          {row.cells.map((cell, index) => (
            <td
              key={index}
              className={`compare__cell ${row.differs ? "compare__cell--diff" : ""}`.trim()}
            >
              {cell.present ? cell.text : <span className="u-muted">-</span>}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
